import { DEFAULT_CHART_OPTIONS } from "entities/charts/options";

// The shared slider styling (transparent track, 14px grip handles, zlevel -1),
// taken from the wells defaults rather than restated, so this widget's gliders
// look and behave like every other chart's.
const defaultSlider = (id: string) =>
  ((DEFAULT_CHART_OPTIONS.dataZoom ?? []) as GenericObject[]).find(
    (dz) => dz.id === id
  ) ?? {};

const DEFAULT_X_SLIDER = defaultSlider("xAxis-slider");
const DEFAULT_Y_SLIDER = defaultSlider("yAxis-slider");

/**
 * Slider thickness. The x slider's TOP edge sits on the axis line
 * (`bottom = grid.bottom − height`) so its handles read as part of the axis
 * rather than floating below the labels; its track is transparent, so the label
 * band it overlaps stays legible. Same treatment the wells and rig/frac charts
 * use.
 */
export const X_SLIDER_HEIGHT = 21;

/** How far inside the left inset the vertical slider sits, clear of the lock handles. */
const Y_SLIDER_INSET = 16;

interface GliderGrid {
  top: number;
  left: number;
  bottom: number;
}

/**
 * Gliders (dataZoom) for the Well Status charts: a slider on each axis plus the
 * matching scroll/pinch `inside` zooms.
 *
 * `filterMode: "none"` throughout — zooming must reframe the axes, never drop
 * data. With the default ("filter") a stacked bar partially outside the window
 * would have its clipped series REMOVED, changing the remaining stack totals and
 * silently redrawing bars at the wrong height.
 *
 * Every entry pins its axis index explicitly. An unpinned dataZoom targets ALL
 * parallel axes of that orientation and range-links the sliders that share it —
 * harmless on these single-axis-per-orientation charts today, but the pin is what
 * stops a future second axis from inheriting the zoom by accident.
 *
 * Both charts get both sliders, whichever way their bars run: Status Over Time
 * zooms time (x) and count (y); Current Well Status zooms count (x) and scrolls
 * the ranked focus bins (y), which is what makes a 20-bin focus readable.
 */
export function buildWellStatusGliders(grid: GliderGrid): GenericObject[] {
  return [
    {
      ...DEFAULT_X_SLIDER,
      xAxisIndex: 0,
      bottom: grid.bottom - X_SLIDER_HEIGHT,
      height: `${X_SLIDER_HEIGHT}px`
    },
    {
      ...DEFAULT_Y_SLIDER,
      yAxisIndex: 0,
      left: Math.max(0, grid.left - Y_SLIDER_INSET),
      // Spans exactly the plot area; without explicit bounds ECharts sizes a
      // vertical slider against the whole container and it overhangs the grid.
      top: grid.top,
      bottom: grid.bottom
    },
    { type: "inside", orient: "horizontal", xAxisIndex: 0, filterMode: "none" },
    { type: "inside", orient: "vertical", yAxisIndex: 0, filterMode: "none" }
  ];
}
